import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Hotel, ShieldCheck, User, KeyRound, ArrowRight } from "lucide-react";

import MSG91OtpButton from "@/components/MSG91OtpButton";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const ALL_ROLES = [
  { key: "traveller", title: "Traveller", icon: User, desc: "Mobile OTP", method: "otp_only" },
  { key: "hotel", title: "Hotel", icon: Hotel, desc: "Email & password", method: "password" },
  { key: "admin", title: "Admin", icon: ShieldCheck, desc: "Email & password", method: "password" },
];

// Admin login is ONLY exposed on:
//   1. The `/ops` route (direct URL: swaanstay.com/ops)
//   2. An admin subdomain (ops.swaanstay.com or admin.swaanstay.com)
// The main /login page never shows the Admin tab, keeping the admin surface
// completely out of sight for travellers and hotels.
const isAdminSurface = () => {
  if (typeof window === "undefined") return false;
  try {
    const h = window.location.hostname.toLowerCase();
    if (h === "ops.swaanstay.com" || h === "admin.swaanstay.com") return true;
    if (h.startsWith("ops.") || h.startsWith("admin.")) return true;
  } catch (e) { /* ignore */ }
  return false;
};

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const adminSurface = isAdminSurface();
  // Direct-URL admin access: /ops always shows the Admin tab, so admins can reach
  // the login page even before ops.swaanstay.com DNS is wired.
  const isOpsRoute = location.pathname === "/ops";
  const showAdminTab = adminSurface || isOpsRoute;
  const ROLES = showAdminTab ? ALL_ROLES : ALL_ROLES.filter((r) => r.key !== "admin");
  // Route-driven initial role tab
  const routeTab = location.pathname === "/hotels-portal" ? "hotel"
                 : isOpsRoute ? "admin"
                 : "traveller";
  const [tab, setTab] = useState(routeTab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mfa, setMfa] = useState(null); // { mfa_token, phone_hint, role } after step-1

  useEffect(() => { setTab(routeTab); }, [routeTab]);
  // Reset any pending 2FA state when the user switches role tabs
  useEffect(() => { setMfa(null); }, [tab]);

  useEffect(() => {
    if (!loading && user) {
      if (!user.role) navigate("/onboarding", { replace: true });
      else if (user.role === "admin") navigate("/admin", { replace: true });
      else if (user.role === "hotel_partner") navigate("/partner", { replace: true });
      else navigate("/customer", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Email and password required");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Step-up: admin & hotel accounts on untrusted devices must complete OTP
      if (data?.mfa_required) {
        setMfa({ mfa_token: data.mfa_token, phone_hint: data.phone_hint, role: data.role });
        toast.message("One more step", { description: `We'll verify an OTP sent to ${data.phone_hint}` });
        setSubmitting(false);
        return;
      }
      if (data?.session_token) localStorage.setItem("hw_session_token", data.session_token);
      // Enforce role for the chosen tab
      if (tab === "admin" && data.role !== "admin") {
        toast.error("This account is not an admin. Try the Hotel tab.");
        setSubmitting(false);
        return;
      }
      if (tab === "hotel" && data.role !== "hotel_partner") {
        toast.error("This account is not a hotel partner. Try the Traveller tab.");
        setSubmitting(false);
        return;
      }
      if (tab === "traveller" && data.role !== "customer") {
        toast.error("This account is not a traveller. Try a different tab.");
        setSubmitting(false);
        return;
      }
      setUser(data);
      toast.success(`Welcome, ${data.name}!`);
      navigate(data.role === "admin" ? "/admin" : data.role === "hotel_partner" ? "/partner" : "/customer");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isNetwork = !err?.response;
      if (isNetwork) {
        toast.error("Can't reach the server. Please check your internet or refresh the page.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (err?.response?.status === 429) {
        toast.error("Too many attempts. Please try again in 15 minutes.");
      } else {
        toast.error("Login failed — please double-check your email and password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const current = ROLES.find((r) => r.key === tab);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="bg-slate-900 text-white p-10 md:p-16 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-80 h-80 bg-emerald-600/20 rounded-full blur-3xl" aria-hidden />
        <div>
          <div className="hw-label text-emerald-400">Welcome to SwaanStay</div>
          <h1 className="hw-head text-4xl md:text-5xl font-black mt-4 leading-tight">
            Pick your role.<br />Sign in.
          </h1>
          <p className="mt-5 text-slate-300 max-w-md leading-relaxed">
            Travellers sign in with <span className="text-emerald-400 font-semibold">Mobile OTP</span>. Hotels use <span className="text-emerald-400 font-semibold">email + password</span>.
          </p>

          <div className="mt-10 space-y-3">
            <Link to="/register/hotel" data-testid="link-hotel-register" className="hw-card bg-white/5 border-white/10 p-4 flex items-center justify-between hover:bg-white/10 transition-all block">
              <div>
                <div className="hw-label text-emerald-400">Are you a hotel?</div>
                <div className="hw-head font-bold text-lg">List your hotel for free</div>
              </div>
              <ArrowRight className="w-5 h-5 text-emerald-400" />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-12">
          <Stat n="100%" l="Secure" />
          <Stat n="0%" l="Commission" />
          <Stat n="6+" l="Cities" />
        </div>
      </div>

      {/* Right panel */}
      <div className="p-8 md:p-16 flex items-center justify-center bg-slate-50">
        <div className="w-full max-w-md">
          {/* Role tabs */}
          <div className="grid grid-cols-3 gap-2 bg-white border border-slate-200 p-1.5 rounded-sm">
            {ROLES.map((r) => (
              <button
                key={r.key}
                data-testid={`login-tab-${r.key}`}
                onClick={() => setTab(r.key)}
                className={`py-3 px-2 text-center transition-colors rounded-sm ${tab === r.key ? "bg-slate-900 text-white" : "bg-transparent text-slate-700 hover:bg-slate-100"}`}
              >
                <r.icon className="w-4 h-4 mx-auto" />
                <div className="hw-label mt-1.5 text-[10px]">{r.title}</div>
              </button>
            ))}
          </div>

          <div className="hw-card p-7 mt-4">
            <div className="hw-label text-blue-700">{current.title} login</div>
            <h2 className="hw-head text-2xl md:text-3xl font-black mt-2 text-slate-900">{current.desc}</h2>

            {current.method === "google" ? (
              <>
                <Button onClick={handleGoogleLogin} data-testid="google-login-btn" className="w-full mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold py-6 h-auto text-base">
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24"><path fill="#FFC107" d="M21.8 10.2H12v3.9h5.7c-.5 2.7-3 4.7-5.7 4.7-3.5 0-6.3-2.8-6.3-6.3S8.5 6.2 12 6.2c1.6 0 3 .6 4.2 1.5l2.7-2.7C17 3.5 14.7 2.5 12 2.5 6.7 2.5 2.5 6.7 2.5 12s4.2 9.5 9.5 9.5c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.3-.4-1.9z"/></svg>
                  Continue with Google
                </Button>
                <p className="text-xs text-slate-500 mt-4">
                  After signing in you&apos;ll choose if you&apos;re a traveller or a hotel partner.
                </p>
              </>
            ) : current.method === "otp_only" ? (
              <>
                <MSG91OtpButton />
                <p className="text-xs text-slate-500 mt-4 text-center">
                  Sign up &amp; log in with the same phone number. No password, no email needed.
                </p>
              </>
            ) : current.method === "either" ? (
              <>
                <MSG91OtpButton />
                <div className="flex items-center gap-3 my-4">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="hw-label text-slate-400">OR</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
                <Button onClick={handleGoogleLogin} data-testid="google-login-btn" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold py-5 h-auto text-base">
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24"><path fill="#FFC107" d="M21.8 10.2H12v3.9h5.7c-.5 2.7-3 4.7-5.7 4.7-3.5 0-6.3-2.8-6.3-6.3S8.5 6.2 12 6.2c1.6 0 3 .6 4.2 1.5l2.7-2.7C17 3.5 14.7 2.5 12 2.5 6.7 2.5 2.5 6.7 2.5 12s4.2 9.5 9.5 9.5c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.3-.4-1.9z"/></svg>
                  Continue with Google
                </Button>
                <details className="mt-4" data-testid="traveller-password-toggle">
                  <summary className="hw-label text-slate-500 cursor-pointer hover:text-slate-700 select-none">Or sign in with email &amp; password</summary>
                  <form onSubmit={handlePasswordLogin} className="space-y-3 mt-3" data-testid="traveller-login-form">
                    <div>
                      <Label className="hw-label">Email</Label>
                      <Input data-testid="traveller-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value.trim())} className="rounded-sm mt-1" placeholder="you@example.com" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" required />
                    </div>
                    <div>
                      <Label className="hw-label">Password</Label>
                      <Input data-testid="traveller-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1" autoComplete="current-password" required />
                    </div>
                    <Button type="submit" disabled={submitting} data-testid="traveller-submit-btn" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold py-5 h-auto">
                      <KeyRound className="w-4 h-4 mr-2" /> {submitting ? "Signing in…" : "Sign in"}
                    </Button>
                  </form>
                </details>
                <p className="text-xs text-slate-500 mt-3 text-center">
                  New traveller?{" "}
                  <Link to="/register/traveller" className="text-blue-700 font-semibold hover:underline" data-testid="link-register-traveller">
                    Create an account
                  </Link>
                </p>
              </>
            ) : (
              mfa ? (
                <div className="space-y-4 mt-6" data-testid={`${tab}-mfa-step`}>
                  <div className="hw-card bg-emerald-50 border-emerald-200 p-4">
                    <div className="hw-label text-emerald-700">Two-factor required</div>
                    <div className="text-sm mt-1 text-slate-800">
                      For extra security, we&apos;ll send an OTP to <span className="font-bold" data-testid="mfa-phone-hint">{mfa.phone_hint}</span>.
                      This device will be trusted for the next 7 days after you verify.
                    </div>
                  </div>
                  <MSG91OtpButton
                    intent="2fa"
                    mfaToken={mfa.mfa_token}
                    label="Verify OTP to sign in"
                    redirectTo={mfa.role === "admin" ? "/admin" : "/partner"}
                  />
                  <button
                    type="button"
                    onClick={() => { setMfa(null); setPassword(""); }}
                    data-testid="mfa-back-btn"
                    className="w-full text-xs text-slate-500 underline hover:text-slate-700"
                  >
                    Use a different account
                  </button>
                </div>
              ) : (
              <form onSubmit={handlePasswordLogin} className="space-y-3 mt-6" data-testid={`${tab}-login-form`}>
                <div>
                  <Label className="hw-label">Email</Label>
                  <Input data-testid={`${tab}-email-input`} type="email" value={email} onChange={(e) => setEmail(e.target.value.trim())} className="rounded-sm mt-1" placeholder="you@hotel.com" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" required />
                </div>
                <div>
                  <Label className="hw-label">Password</Label>
                  <Input data-testid={`${tab}-password-input`} type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1" autoComplete="current-password" required />
                </div>
                <Button type="submit" disabled={submitting} data-testid={`${tab}-submit-btn`} className="w-full bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold py-5 h-auto">
                  <KeyRound className="w-4 h-4 mr-2" /> {submitting ? "Signing in…" : `Sign in as ${current.title}`}
                </Button>
                {tab === "hotel" && (
                  <>
                    <div className="flex items-center gap-3 my-4">
                      <div className="h-px flex-1 bg-slate-200" />
                      <span className="hw-label text-slate-400">OR</span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                    <MSG91OtpButton intent="partner" />
                    <p className="text-xs text-slate-500 mt-3 text-center">
                      New hotel? Sign up with Mobile OTP above — you&apos;ll be reviewed by our team before going live.
                    </p>
                  </>
                )}
              </form>
              )
            )}

            <p className="text-xs text-slate-500 mt-6 text-center">
              <Link to="/" className="text-blue-700 hover:underline">← Back to home</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border-l-2 border-blue-500 pl-3">
      <div className="hw-head text-2xl font-black">{n}</div>
      <div className="hw-label text-slate-400">{l}</div>
    </div>
  );
}
